import { useEffect, useRef } from "react";

const DEFAULT_EVENTS = ["pointerdown", "touchstart"];

// is slightly different than the mantine one that didn't work for the feature in TimeProcessStepForm
export function useClickOutside<T extends HTMLElement>(
  callback: () => void,
  events?: string[] | null,
  ignoredRefs?: React.RefObject<HTMLDivElement | null>[],
) {
  const ref = useRef<T>(null);
  const eventsList = events || DEFAULT_EVENTS;

  useEffect(() => {
    const listener = (event: Event) => {
      const target = event.target as HTMLElement | null;

      if (!target || !document.body.contains(target)) {
        return;
      }

      // Check if the click is inside the main element
      if (ref.current?.contains(target)) {
        return;
      }

      // Check if the click is inside any of the ignored refs
      if (ignoredRefs && ignoredRefs.length > 0) {
        const isInsideIgnoredNode = ignoredRefs.some(
          (refObj) => refObj.current && refObj.current.contains(target),
        );

        if (isInsideIgnoredNode) {
          return;
        }
      }

      // Check for common dropdown/menu elements that should be ignored
      if (
        target.closest(".mantine-Menu-dropdown") ||
        target.closest(".mantine-Popover-dropdown") ||
        target.closest('[role="menu"]') ||
        target.closest('[role="listbox"]') ||
        target.closest("[data-mantine-stop-propagation]")
      ) {
        return;
      }

      // If we get here, the click was outside the main element and outside ignored nodes
      callback();
    };

    eventsList.forEach((eventName) =>
      document.addEventListener(eventName, listener, { capture: true }),
    );

    return () => {
      eventsList.forEach((eventName) =>
        document.removeEventListener(eventName, listener, { capture: true }),
      );
    };
  }, [callback, ignoredRefs, eventsList]);

  return ref;
}
